/**
 * Component: SectionContent
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R05, BROWSE-V1-R07, BROWSE-V1-R08
 *
 * Description: Renders one entry from the upstream
 *              `fullContent.sectionContent[]` array as a
 *              standalone `<section>` with the DOM contract
 *              `id="cnt_<sectionId>"`. The heading tag is derived
 *              from `sectionHierarchy`:
 *                level 1 -> h2
 *                level 2 -> h3
 *                level 3+ -> h4
 *              so screen-reader and keyboard-nav heading outlines
 *              match the guideline's actual nesting (the viewer's
 *              own page title occupies h1).
 *
 *              `sectionText` is sanitised via the shared SafeHtml
 *              component (which wraps DOMPurify with the existing
 *              guideline-content allow-list shared with Search +
 *              Chat) so a malicious upstream HTML payload cannot
 *              ever inject scripts or event handlers.
 *
 *              BROWSE-V1-R07: any `recommendationContent[]`
 *              entries are narrowed via
 *              `extractRecommendationsFromSection` (drops broken
 *              entries silently) and rendered as one
 *              `<RecommendationCard>` per narrowed entry. The
 *              parent passes `colourOption` (the guideline-level
 *              `recommendationColourOption` mode selector) so
 *              every card in a guideline renders under the same
 *              mode.
 *
 * Risk Links: R9, R7
 * FMEA Links: FM9, FM7
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SafeHtml } from '@/components/SafeHtml';
import {
  extractRecommendationsFromSection,
  type ReferenceEntry,
  type SectionContentEntry,
} from '../types';
import { RecommendationCard } from './RecommendationCard';
import { ReferencePopover } from './ReferencePopover';

interface SectionContentProps {
  section: SectionContentEntry;
  /**
   * Guideline-level `recommendationColourOption` forwarded from
   * ViewerPage. Null when the upstream payload omits the field.
   */
  colourOption: string | null;
  /**
   * BROWSE-V1-R08: Lookup of narrowed `referenceContent[]` keyed by
   * string `referenceId`. SectionContent attaches a click handler
   * to every anchor inside the sanitised `sectionText` body that
   * carries a resolvable id, and opens the matching entry in a
   * `ReferencePopover` on click.
   */
  referenceMap?: Map<string, ReferenceEntry>;
}

interface ActiveReference {
  anchor: HTMLAnchorElement;
  reference: ReferenceEntry;
}

/**
 * BROWSE-V1-R08: Resolve an anchor element to a reference id. The
 * upstream payload is not strictly documented on exactly how inline
 * citations are marked, so we accept two conventions:
 *
 *   1. `<a data-reference-id="42">` — explicit data attribute
 *      (preferred; SafeHtml allow-lists the attribute for this
 *      reason).
 *   2. `<a href="#reference-42">` — fragment form sometimes used
 *      by older guideline payloads.
 *
 * Returns the string form of the id or null if the anchor isn't a
 * resolvable citation reference.
 */
function resolveReferenceId(anchor: HTMLAnchorElement): string | null {
  const dataAttr = anchor.getAttribute('data-reference-id');
  if (dataAttr && dataAttr.length > 0) return dataAttr;
  const href = anchor.getAttribute('href') || '';
  const match = href.match(/^#reference-(.+)$/);
  return match && match[1] ? match[1] : null;
}

type HeadingTag = 'h2' | 'h3' | 'h4';

function headingTagFor(hierarchy: number): HeadingTag {
  if (hierarchy <= 1) return 'h2';
  if (hierarchy === 2) return 'h3';
  return 'h4';
}

export function SectionContent({
  section,
  colourOption,
  referenceMap,
}: SectionContentProps): React.ReactElement {
  const Heading = headingTagFor(section.sectionHierarchy);
  // BROWSE-V1-R07: Narrow the raw recommendationContent[] once per
  // render. extractRecommendationsFromSection drops malformed
  // entries silently so a single broken recommendation can't bring
  // down the rest of the section.
  const recommendations = useMemo(
    () => extractRecommendationsFromSection(section),
    [section],
  );

  // BROWSE-V1-R08: Use event delegation on the body container so
  // click handlers survive dangerouslySetInnerHTML DOM replacements
  // caused by re-renders (e.g. when activeReference state changes).
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [activeReference, setActiveReference] = useState<ActiveReference | null>(null);

  const handleClosePopover = useCallback(() => {
    setActiveReference(null);
  }, []);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const handleBodyClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      // Walk up to find the nearest <a> ancestor within the body.
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor || !body.contains(anchor)) return;

      // Try the referenceMap lookup first (data-reference-id or #reference- href).
      const refId = resolveReferenceId(anchor);
      if (refId !== null && referenceMap) {
        const reference = referenceMap.get(refId);
        if (!reference) return;
        event.preventDefault();
        setActiveReference({ anchor, reference });
        return;
      }

      // Upstream uses data-toggle="popover" with inline data-content HTML.
      if (anchor.getAttribute('data-toggle') === 'popover') {
        const dataContent = anchor.getAttribute('data-content');
        if (!dataContent) return;
        event.preventDefault();
        setActiveReference({
          anchor,
          reference: {
            referenceId: anchor.textContent?.replace(/[()]/g, '').trim() || '?',
            referenceText: '',
            html: dataContent,
          },
        });
      }
    };

    body.addEventListener('click', handleBodyClick);
    return () => {
      body.removeEventListener('click', handleBodyClick);
    };
  }, [referenceMap]);

  return (
    <section
      id={`cnt_${section.sectionId}`}
      data-testid={`section-${section.sectionId}`}
      aria-labelledby={`section-heading-${section.sectionId}`}
      className="space-y-3 py-4"
    >
      <Heading
        id={`section-heading-${section.sectionId}`}
        className="scroll-mt-20 text-lg font-semibold text-foreground"
      >
        {section.sectionName}
      </Heading>
      {section.sectionText && (
        <div ref={bodyRef} className="[&_a[data-toggle=popover]]:cursor-pointer [&_a[data-toggle=popover]]:text-primary [&_a[data-toggle=popover]]:underline">
          <SafeHtml
            html={section.sectionText}
            className="prose prose-sm max-w-none text-foreground"
          />
        </div>
      )}
      {activeReference && (
        <ReferencePopover
          anchor={activeReference.anchor}
          reference={activeReference.reference}
          onClose={handleClosePopover}
        />
      )}
      {recommendations.length > 0 && (
        <div
          data-testid={`section-${section.sectionId}-recommendations`}
          className="flex flex-col gap-3"
        >
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.recommendationId}
              recommendation={rec}
              colourOption={colourOption}
            />
          ))}
        </div>
      )}
    </section>
  );
}
